import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import heroSlideService from '../../services/heroSlideService';
import { HeroSlideCard } from '../../components/carousel';
import HeroSlideEditModal from './HeroSlideEditModal';
import './HeroCarouselPage.css';

function HeroCarouselPage() {
  // State
  const [slides, setSlides] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSlide, setSelectedSlide] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [updatingSlideId, setUpdatingSlideId] = useState(null);

  // Drag state
  const [draggedSlide, setDraggedSlide] = useState(null);
  const [dragOverSlideId, setDragOverSlideId] = useState(null);

  // Fetch slides on mount
  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await heroSlideService.getHomeSlides();
      // Sort by displayOrder
      const sortedSlides = (response.data || []).sort(
        (a, b) => a.displayOrder - b.displayOrder
      );
      setSlides(sortedSlides);
    } catch (err) {
      console.error('Failed to fetch slides:', err);
      setError('Failed to load carousel slides. Please try again.');
      toast.error('Failed to load slides');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit button click
  const handleEdit = useCallback((slide) => {
    setSelectedSlide(slide);
    setIsModalOpen(true);
  }, []);

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedSlide(null);
  }, []);

  // Handle save from modal
  const handleSave = async (id, data) => {
    setIsSaving(true);
    try {
      const response = await heroSlideService.updateSlide(id, data);
      const updated = response.data;
      setSlides((prev) =>
        prev.map((s) => (s.id === id ? updated : s)).sort((a, b) => a.displayOrder - b.displayOrder)
      );
      toast.success('Slide updated successfully');
      handleCloseModal();
    } catch (err) {
      console.error('Failed to update slide:', err);
      const message = err.response?.data?.error || 'Failed to update slide';
      toast.error(message);
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  // Handle toggle active
  const handleToggleActive = async (id) => {
    setUpdatingSlideId(id);
    try {
      const response = await heroSlideService.toggleActive(id);
      const updated = response.data;
      setSlides((prev) =>
        prev.map((s) => (s.id === id ? updated : s))
      );
      toast.success(`Slide ${updated.isActive ? 'activated' : 'deactivated'}`);
    } catch (err) {
      console.error('Failed to toggle slide:', err);
      toast.error('Failed to toggle slide status');
    } finally {
      setUpdatingSlideId(null);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, slide) => {
    setDraggedSlide(slide);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, slide) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (slide.id !== draggedSlide?.id) {
      setDragOverSlideId(slide.id);
    }
  };

  const handleDragLeave = () => {
    setDragOverSlideId(null);
  };

  const handleDrop = async (e, targetSlide) => {
    e.preventDefault();

    if (!draggedSlide || draggedSlide.id === targetSlide.id) {
      resetDragState();
      return;
    }

    // Calculate new order
    const newSlides = [...slides];
    const draggedIndex = newSlides.findIndex((s) => s.id === draggedSlide.id);
    const targetIndex = newSlides.findIndex((s) => s.id === targetSlide.id);

    // Remove dragged item and insert at target position
    newSlides.splice(draggedIndex, 1);
    newSlides.splice(targetIndex, 0, draggedSlide);

    // Update display orders locally
    const reorderedSlides = newSlides.map((s, i) => ({
      ...s,
      displayOrder: i + 1,
    }));

    // Optimistic update
    setSlides(reorderedSlides);
    resetDragState();

    // Get ordered IDs for API call
    const orderedIds = reorderedSlides.map((s) => s.id);

    try {
      const response = await heroSlideService.reorderHomeSlides(orderedIds);
      const updatedSlides = (response.data || []).sort(
        (a, b) => a.displayOrder - b.displayOrder
      );
      setSlides(updatedSlides);
      toast.success('Slides reordered successfully');
    } catch (err) {
      console.error('Failed to reorder slides:', err);
      toast.error('Failed to reorder slides');
      // Revert to original order
      fetchSlides();
    }
  };

  const handleDragEnd = () => {
    resetDragState();
  };

  const resetDragState = () => {
    setDraggedSlide(null);
    setDragOverSlideId(null);
  };

  // Keyboard reordering handler
  const handleKeyDown = async (e, _slide, index) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
      return;
    }

    e.preventDefault();

    const newIndex = e.key === 'ArrowLeft' ? index - 1 : index + 1;

    // Check bounds
    if (newIndex < 0 || newIndex >= slides.length) {
      return;
    }

    // Calculate new order
    const newSlides = [...slides];
    const [movedSlide] = newSlides.splice(index, 1);
    newSlides.splice(newIndex, 0, movedSlide);

    // Update display orders locally
    const reorderedSlides = newSlides.map((s, i) => ({
      ...s,
      displayOrder: i + 1,
    }));

    // Optimistic update
    setSlides(reorderedSlides);

    // Focus the moved element after re-render
    setTimeout(() => {
      const wrappers = document.querySelectorAll('.slide-wrapper');
      wrappers[newIndex]?.focus();
    }, 0);

    // Get ordered IDs for API call
    const orderedIds = reorderedSlides.map((s) => s.id);

    try {
      const response = await heroSlideService.reorderHomeSlides(orderedIds);
      const updatedSlides = (response.data || []).sort(
        (a, b) => a.displayOrder - b.displayOrder
      );
      setSlides(updatedSlides);
      toast.success('Slides reordered successfully');
    } catch (err) {
      console.error('Failed to reorder slides:', err);
      toast.error('Failed to reorder slides');
      fetchSlides();
    }
  };

  // Calculate stats
  const stats = {
    total: slides.length,
    active: slides.filter((s) => s.isActive).length,
    inactive: slides.filter((s) => !s.isActive).length,
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="hero-carousel-page">
        <div className="page-header">
          <div>
            <div className="skeleton skeleton-title" />
            <div className="skeleton skeleton-subtitle" />
          </div>
        </div>
        <div className="stats-cards">
          {[1, 2, 3].map((i) => (
            <div key={i} className="stat-card skeleton-stat" />
          ))}
        </div>
        <div className="slides-grid">
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton-slide-card" />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="hero-carousel-page">
        <div className="error-state">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1}
            stroke="currentColor"
            className="error-icon"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
            />
          </svg>
          <h3>Error Loading Slides</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchSlides}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-carousel-page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1>Hero Carousel</h1>
          <p>Manage the hero carousel slides displayed on the home page</p>
        </div>
        <a
          href={process.env.REACT_APP_ECOMMERCE_URL || 'http://localhost:3000'}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-outline"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="btn-icon"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
            />
          </svg>
          View Live Site
        </a>
      </div>

      {/* Stats Cards */}
      <div className="stats-cards">
        <div className="stat-card">
          <span className="stat-value">{stats.total}</span>
          <span className="stat-label">TOTAL SLIDES</span>
        </div>
        <div className="stat-card stat-card--success">
          <span className="stat-value">{stats.active}</span>
          <span className="stat-label">ACTIVE</span>
        </div>
        <div className="stat-card stat-card--warning">
          <span className="stat-value">{stats.inactive}</span>
          <span className="stat-label">INACTIVE</span>
        </div>
      </div>

      {/* Info Banner */}
      <div className="info-banner">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="info-icon"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
          />
        </svg>
        <p>
          The carousel displays exactly <strong>3 slides</strong> on the home page.
          Drag cards to reorder, or use <strong>arrow keys</strong> when focused. Changes sync automatically to the live site.
        </p>
      </div>

      {/* Slides Grid */}
      <div className={`slides-grid ${draggedSlide ? 'is-dragging' : ''}`} role="list">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`slide-wrapper ${dragOverSlideId === slide.id ? 'drag-over' : ''} ${draggedSlide?.id === slide.id ? 'dragging' : ''}`}
            draggable
            onDragStart={(e) => handleDragStart(e, slide)}
            onDragOver={(e) => handleDragOver(e, slide)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, slide)}
            onDragEnd={handleDragEnd}
            tabIndex={0}
            onKeyDown={(e) => handleKeyDown(e, slide, index)}
            role="listitem"
            aria-label={`Slide ${index + 1}: ${slide.title}. Press arrow keys to reorder.`}
          >
            <span className="slide-order-badge">{index + 1}</span>
            <HeroSlideCard
              slide={slide}
              onEdit={handleEdit}
              onToggleActive={handleToggleActive}
              isUpdating={updatingSlideId === slide.id}
              isDragging={draggedSlide?.id === slide.id}
              isLastActive={stats.active === 1 && slide.isActive}
            />
          </div>
        ))}
      </div>

      {/* Empty State */}
      {slides.length === 0 && (
        <div className="empty-state">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1}
            stroke="currentColor"
            className="empty-icon"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
            />
          </svg>
          <h3>No slides found</h3>
          <p>Hero slides have not been configured yet.</p>
        </div>
      )}

      {/* Edit Modal */}
      {isModalOpen && selectedSlide && (
        <HeroSlideEditModal
          slide={selectedSlide}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSave}
          isSaving={isSaving}
        />
      )}
    </div>
  );
}

export default HeroCarouselPage;
